import { useEffect, useRef, useState } from 'react'
import { registerStepUpAsker } from '@/lib/api/step-up'
import { devicesApi, type Device } from '../api'
import { StepUpDialog } from './StepUpDialog'

type Earned = { token: string; expiresInSeconds: number; strong: boolean } | null

/**
 * The screen that asks "confirm it's you" when the server refuses a high-risk action for want of
 * a fresh proof (MFA build plan, Stage 7). Mounted once, for a signed-in user.
 *
 * It does nothing until asked (lib/api/step-up.ts): no request, no dialog. Asked, it reads the
 * user's devices, shows the dialog - a passkey or security key only, where the server wants a
 * strong proof - and answers with the token earned, or with nothing if the user closes it.
 */
export function StepUpGate() {
    const [asking, setAsking] = useState<{ strongOnly: boolean; devices: Device[] } | null>(null)
    const answer = useRef<((earned: Earned) => void) | null>(null)
    const settle = (earned: Earned) => {
        const resolve = answer.current
        answer.current = null
        setAsking(null)
        resolve?.(earned)
    }

    useEffect(() => registerStepUpAsker(async (strongOnly) => {
        // One question at a time: a second refused request while the dialog is open gets no proof
        // of its own and goes back to its caller.
        if (answer.current) return null
        let devices: Device[] = []
        try { devices = await devicesApi.list() } catch { return null }
        return new Promise<Earned>((resolve) => {
            answer.current = resolve
            setAsking({ strongOnly, devices })
        })
    }), [])
    // Unmounted mid-question (signed out): the request waiting on it is told there is no proof.
    useEffect(() => () => { answer.current?.(null); answer.current = null }, [])

    if (!asking) return null
    return (
        <StepUpDialog
            open
            devices={asking.devices}
            strongOnly={asking.strongOnly}
            onClose={() => settle(null)}
            onSteppedUp={(token, expiresInSeconds, strong) => settle({ token, expiresInSeconds, strong })}
        />
    )
}
